import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  api, SALE, ESAMI, esamiAttivi, saleRichieste, STATO_STYLE, EVENTO_LABEL,
  openReportPdf, openReportExcel, openScheda, todayISO, formatDateTime,
  tempiVisita, medieTempi, minuti,
} from "@/lib/clinic";
import { useQueue } from "@/hooks/useQueue";
import { Logo } from "@/components/Logo";
import { WorkdayPicker } from "@/components/WorkdayPicker";
import { ArrowLeft, Printer, FileText, Loader2, Users, UserCheck, CheckCircle2, UserX, Sheet, Timer, ChevronDown } from "lucide-react";

const Kpi = ({ icon: Icon, label, value, tone }) => (
  <div className="relative rounded-xl border border-slate-200 bg-white p-5 overflow-hidden">
    <div className={`absolute left-0 top-0 h-full w-1 ${tone}`} />
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</div>
        <div className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-1">{value}</div>
      </div>
      <Icon className="w-5 h-5 text-slate-400" />
    </div>
  </div>
);

const Media = ({ label, value, nota }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
    <div className="font-display text-2xl font-bold tracking-tight text-sky-900">{value}</div>
    {nota && <div className="text-[10px] text-slate-400">{nota}</div>}
  </div>
);

export default function Report() {
  const { patients, medico, lastEvent } = useQueue();
  const [giorno, setGiorno] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tempiAperti, setTempiAperti] = useState(false);

  const conteggioGiorni = useMemo(() => {
    const acc = {};
    patients.forEach((p) => {
      if (p.data) acc[p.data] = (acc[p.data] || 0) + 1;
    });
    return acc;
  }, [patients]);
  const giorni = useMemo(() => Object.keys(conteggioGiorni).sort(), [conteggioGiorni]);

  // Nuvolette rapide: solo le ultime 3 giornate con pazienti (per le altre c'è il calendario).
  const giorniChip = useMemo(() => {
    const ultimi = giorni.slice(-3);
    return giorni.includes(giorno) && !ultimi.includes(giorno) ? [giorno, ...ultimi] : ultimi;
  }, [giorni, giorno]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/report", { params: { data: giorno } })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [giorno, lastEvent]);

  useEffect(() => {
    if (giorni.length && !giorni.includes(giorno) && !giorni.includes(todayISO())) setGiorno(giorni[0]);
  }, [giorni, giorno]);

  const totali = data?.totali || { elenco: 0, accettati: 0, completati: 0, assenti: 0 };
  const elenco = data?.patients || [];
  const medie = useMemo(() => medieTempi(elenco), [elenco]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-4 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-4">
            <Logo className="h-8 sm:h-9" />
            <div className="h-9 w-px bg-slate-200" />
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900 leading-none">
                Report Giornata
              </h1>
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.28em] text-slate-400">
                Attività e accertamenti
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {medico && (
              <Badge variant="outline" className="rounded-full border-sky-300 text-sky-800 bg-sky-50" data-testid="report-medico-badge">
                {medico}
              </Badge>
            )}
            <Button asChild variant="outline" className="rounded-full" data-testid="report-back-link">
              <Link to="/"><ArrowLeft className="w-4 h-4 mr-2" /> Gestione</Link>
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => openReportExcel(giorno)} data-testid="report-excel-btn">
              <Sheet className="w-4 h-4 mr-2" /> Esporta Excel
            </Button>
            <Button className="rounded-full" onClick={() => openReportPdf(giorno)} data-testid="report-print-btn">
              <Printer className="w-4 h-4 mr-2" /> Stampa report
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-5 sm:px-8 py-7 space-y-7">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <div className="text-xs uppercase tracking-widest text-slate-500">Giornata</div>
            <WorkdayPicker
              value={giorno}
              onChange={setGiorno}
              counts={conteggioGiorni}
              testId="report-date-picker"
            />
          </div>
          <div className="flex flex-wrap gap-1.5 pb-1" data-testid="report-day-chips">
            {giorniChip.map((d) => (
              <button
                key={d}
                onClick={() => setGiorno(d)}
                data-testid={`report-day-chip-${d}`}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  d === giorno ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                }`}
              >
                {new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
              </button>
            ))}
          </div>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400 mb-3" />}
          <button
            onClick={() => setTempiAperti((v) => !v)}
            data-testid="report-tempi-toggle"
            className={`ml-auto mb-1 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              tempiAperti
                ? "bg-sky-700 text-white border-sky-700"
                : "bg-white border-sky-300 text-sky-800 hover:border-sky-500"
            }`}
          >
            <Timer className="w-3.5 h-3.5" /> Tempi di visita
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${tempiAperti ? "rotate-180" : ""}`} />
          </button>
        </div>

        {tempiAperti && (
          <div className="rounded-xl border border-sky-200 bg-white overflow-hidden" data-testid="report-tempi-panel">
            <div className="grid gap-4 border-b border-sky-100 bg-sky-50/70 px-5 py-4 sm:grid-cols-3 lg:grid-cols-5">
              <Media label="Tempo medio totale" value={minuti(medie.totale)} nota={`${medie.rilevati} visite rilevate`} />
              <Media label="Attesa media" value={minuti(medie.attesa)} />
              {Object.keys(SALE).map((r) => (
                <Media key={r} label={`Media ${SALE[r].short}`} value={minuti(medie.sale[r])} />
              ))}
              <Media label="Media in visita" value={minuti(medie.visita)} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[1000px]">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-[11px] uppercase tracking-widest text-slate-500">
                    <th className="px-5 py-3 font-semibold">Codice</th>
                    <th className="px-5 py-3 font-semibold">Dipendente</th>
                    <th className="px-4 py-3 font-semibold">Arrivo</th>
                    {Object.keys(SALE).map((r) => (
                      <th key={r} colSpan={3} className="px-4 py-3 font-semibold text-center border-l border-slate-200">
                        {SALE[r].short}
                      </th>
                    ))}
                    <th className="px-4 py-3 font-semibold border-l border-slate-200">Attesa</th>
                    <th className="px-4 py-3 font-semibold">In visita</th>
                    <th className="px-4 py-3 font-semibold">Totale</th>
                  </tr>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                    <th /><th /><th />
                    {Object.keys(SALE).map((r) => (
                      <Fragment key={r}>
                        <th className="px-4 pb-2 font-medium border-l border-slate-200">Inizio</th>
                        <th className="px-4 pb-2 font-medium">Fine</th>
                        <th className="px-4 pb-2 font-medium">Durata</th>
                      </Fragment>
                    ))}
                    <th className="border-l border-slate-200" /><th /><th />
                  </tr>
                </thead>
                <tbody>
                  {elenco.map((p) => {
                    const t = tempiVisita(p);
                    return (
                      <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-sky-50/40" data-testid="report-tempi-row">
                        <td className="px-5 py-2.5 font-ticket font-bold text-slate-900">{p.codice || "—"}</td>
                        <td className="px-5 py-2.5 text-sm text-slate-800">
                          {[p.nome, p.cognome].filter(Boolean).join(" ")}
                        </td>
                        <td className="px-4 py-2.5 font-ticket text-sm text-slate-700">{t.arrivo || "—"}</td>
                        {Object.keys(SALE).map((r) => (
                          <Fragment key={r}>
                            <td className="px-4 py-2.5 font-ticket text-sm text-slate-600 border-l border-slate-100">
                              {t.sale[r]?.inizio || "—"}
                            </td>
                            <td className="px-4 py-2.5 font-ticket text-sm text-slate-600">
                              {t.sale[r]?.fine || "—"}
                            </td>
                            <td className="px-4 py-2.5 text-sm font-semibold text-slate-800">
                              {t.sale[r] ? minuti(t.sale[r].durata) : "—"}
                            </td>
                          </Fragment>
                        ))}
                        <td className="px-4 py-2.5 text-sm text-amber-700 border-l border-slate-100">{minuti(t.attesa)}</td>
                        <td className="px-4 py-2.5 text-sm text-slate-800">{minuti(t.visita)}</td>
                        <td className="px-4 py-2.5 text-sm font-bold text-sky-800">{minuti(t.totale)}</td>
                      </tr>
                    );
                  })}
                  {elenco.length === 0 && (
                    <tr>
                      <td colSpan={12} className="px-5 py-12 text-center text-slate-400 font-display text-lg" data-testid="report-tempi-empty">
                        Nessun paziente in questa giornata
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Users} label="In elenco" value={totali.elenco} tone="bg-sky-500" />
          <Kpi icon={UserCheck} label="Accettati" value={totali.accettati} tone="bg-emerald-500" />
          <Kpi icon={CheckCircle2} label="Completati" value={totali.completati} tone="bg-slate-400" />
          <Kpi icon={UserX} label="Assenti" value={totali.assenti} tone="bg-rose-500" />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-lg font-bold text-slate-900">Accertamenti per paziente</h2>
            <div className="flex flex-wrap gap-3 text-xs text-slate-500">
              {Object.entries(ESAMI).map(([k, v]) => (
                <span key={k}>
                  <span className="font-bold text-slate-700">{k}</span> = {v}
                </span>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[1000px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3 font-semibold">Codice</th>
                  <th className="px-5 py-3 font-semibold">Dipendente</th>
                  <th className="px-5 py-3 font-semibold">Azienda</th>
                  <th className="px-5 py-3 font-semibold">Arrivo</th>
                  <th className="px-5 py-3 font-semibold">Sale</th>
                  <th className="px-5 py-3 font-semibold">Esami analizzati</th>
                  <th className="px-5 py-3 font-semibold">Stato</th>
                  <th className="px-5 py-3 font-semibold text-right">Scheda</th>
                </tr>
              </thead>
              <tbody>
                {(data?.patients || []).map((p) => {
                  const sta = STATO_STYLE[p.stato] || STATO_STYLE.da_accettare;
                  const esami = esamiAttivi(p).filter((e) => ESAMI[e.key]);
                  return (
                    <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70" data-testid="report-patient-row">
                      <td className="px-5 py-3 font-ticket font-bold text-slate-900">{p.codice || "—"}</td>
                      <td className="px-5 py-3 text-sm font-medium text-slate-900">
                        {[p.nome, p.cognome].filter(Boolean).join(" ")}
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-600">{p.azienda || "—"}</td>
                      <td className="px-5 py-3 font-ticket text-sm text-slate-700">{p.orario_arrivo || "—"}</td>
                      <td className="px-5 py-3 text-xs text-slate-600">
                        {saleRichieste(p).map((r) => SALE[r].short).join(" + ")}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {esami.map((e) => (
                            <span
                              key={e.key}
                              title={e.valore}
                              data-testid={`report-esame-${e.key}`}
                              className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700"
                            >
                              {e.key} · {e.label}
                            </span>
                          ))}
                          {esami.length === 0 && <span className="text-xs text-slate-300">nessuno</span>}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-block rounded-full border px-2.5 py-1 text-xs font-semibold ${sta.cls}`}>
                          {sta.label}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <Button size="sm" variant="ghost" className="rounded-full" onClick={() => openScheda(p)} data-testid="report-scheda-btn">
                          <FileText className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {(data?.patients || []).length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-14 text-center text-slate-400 font-display text-xl" data-testid="report-empty">
                      Nessun paziente in questa giornata
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="px-5 py-4 border-b border-slate-200">
            <h2 className="font-display text-lg font-bold text-slate-900">Storico attività della giornata</h2>
          </div>
          <ul className="divide-y divide-slate-100" data-testid="report-activity-list">
            {(data?.eventi || []).slice().reverse().map((ev, i) => (
              <li key={i} className="px-5 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid="report-activity-item">
                <span className="font-ticket text-xs text-slate-400 w-[110px]">{formatDateTime(ev.at)}</span>
                <span className="font-ticket font-bold text-slate-900 w-[64px]">{ev.codice || "—"}</span>
                <span className="font-medium text-slate-800 flex-1 min-w-[160px]">{ev.paziente}</span>
                <span className="text-slate-600">
                  {EVENTO_LABEL[ev.tipo] || ev.tipo}
                  {ev.sala ? ` — ${SALE[ev.sala]?.short}` : ""}
                </span>
              </li>
            ))}
            {(data?.eventi || []).length === 0 && (
              <li className="px-5 py-12 text-center text-slate-400 font-display text-lg">Nessuna attività registrata</li>
            )}
          </ul>
        </div>
      </main>
    </div>
  );
}
